import {useCallback, useEffect, useState} from 'react'
import {
  CatalogColumn,
  ColumnFilter,
  SortState,
} from 'src/app/features/listing-product-table-v2/types'
import {payoutApiGet} from './api'
import {appendPayoutColumnFiltersToParams} from './buildPayoutFilterParams'
import {PayoutListResponse, PayoutLogRow} from './types'

interface UsePayoutLogsDataOptions {
  platformId?: string | number
  page: number
  pageSize: number
  globalSearch: string
  columnFilters: ColumnFilter[]
  catalogColumns?: CatalogColumn[]
  sortState: SortState | null
}

export const usePayoutLogsData = ({
  platformId,
  page,
  pageSize,
  globalSearch,
  columnFilters,
  catalogColumns = [],
  sortState,
}: UsePayoutLogsDataOptions) => {
  const [data, setData] = useState<PayoutLogRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('limit', String(pageSize))

      if (platformId) {
        params.append('platform_id', String(platformId))
      }

      if (globalSearch) {
        params.append('search_string', globalSearch)
      }

      if (sortState) {
        params.append('sort_by', sortState.field)
        params.append('sort_order', sortState.direction.toUpperCase())
      }

      appendPayoutColumnFiltersToParams(params, columnFilters, catalogColumns)

      const response: any = await payoutApiGet<PayoutListResponse>(
        `/api/platform-order-payout-list?${params.toString()}`
      )
      const payload = response?.data || response
      const rows = Array.isArray(payload?.rows) ? payload.rows : []
      const normalizedRows = rows.map((row: PayoutLogRow, index: number) => {
        const fallbackId = String(
          row?.id ||
            row?.grouped_payout_id ||
            row?.settlement_id ||
            row?.payout_id ||
            `${row?.channel || row?.platform_id || 'payout'}-${row?.estimated_payout_at || index}`
        )
        return {
          ...row,
          id: row?.id || fallbackId,
          client_id: fallbackId,
          __synthetic_id: !row?.id,
        }
      })

      setData(normalizedRows)
      setTotal(Number(payload?.total ?? normalizedRows.length) || 0)
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to fetch payout logs')
      setData([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [platformId, page, pageSize, globalSearch, columnFilters, catalogColumns, sortState])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {data, total, loading, error, refetch: fetchData}
}
